import React, { useState, useRef, useEffect } from 'react';
import { BaseButton, CloseButton } from '@repo/common';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';

const DropdownField = ({ label, value, options, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  return (
    <div className='flex flex-col items-start gap-2 w-full'>
      <div className='typo-b12Strong text-text-secondary'>{label}</div>
      <div className='relative w-full' ref={containerRef}>
        <BaseButton
          type='text_basic'
          size='s'
          icon={isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
          iconPosition='after'
          separate={true}
          leftalign={true}
          width='100%'
          onClick={() => setIsOpen(!isOpen)}
          bgclass={`${
            isOpen
              ? 'border-surface-green40 border-[2px]'
              : 'border-grayscale-gray02'
          }`}
          textclass='typo-b13Strong text-text-tertiary'
          hoverclass=''
          activeclass=''
        >
          {value}
        </BaseButton>

        {isOpen && (
          <div className='mt-1 w-full bg-grayscale-gray00 border border-grayscale-gray02 rounded-lg p-1.5 overflow-hidden'>
            {options.map((opt) => (
              <div
                key={opt}
                className='p-1.5 typo-b13 text-grayscale-gray07 hover:bg-grayscale-gray01 active:bg-surface-green10 cursor-pointer bg-grayscale-gray00 rounded-[4px]'
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterModal = ({ onClose, coords, onApply, initialValues }) => {
  const [filters, setFilters] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  const optionsData = {
    'Sort by': ['Start date (Newest first)', 'Start date (Oldest first)'],
    'Date range': ['All time', 'This week', 'This month', 'Last 3 months'],
    Category: ['All categories', 'Culture', 'Career', 'etc'],
    Status: [
      'All Statuses',
      'Open',
      'Full',
      'Closed',
      'Ongoing',
      'Completed',
      'Hidden',
    ],
  };

  useEffect(() => {
    setFilters(initialValues);
  }, [initialValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleSelect = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = async () => {
    setIsLoading(true);
    try {
      await onApply(filters);
      onClose();
    } catch (error) {
      console.error('필터 적용 중 오류 발생:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className='fixed inset-0 z-[9998] bg-transparent'
        onClick={onClose}
      />
      <div
        ref={modalRef}
        className='absolute z-[9999]'
        style={{
          top: `${coords.top}px`,
          left: `${coords.left}px`,
          width: '248px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='soft-70 bg-grayscale-gray00 border-grayscale-gray02 border-[1.6px] rounded-[15px] p-4 shadow-popup-04'>
          <div className='flex justify-between items-center pb-4'>
            <div className='typo-b14Strong text-text-primary'>Filters</div>
            <CloseButton type='light' onClick={onClose} />
          </div>

          <div className='flex flex-col gap-4 pb-[30px]'>
            {Object.entries(optionsData).map(([label, options]) => {
              const key =
                label === 'Sort by'
                  ? 'sortBy'
                  : label === 'Date range'
                    ? 'dateRange'
                    : label.toLowerCase();

              return (
                <DropdownField
                  key={label}
                  label={label}
                  value={filters[key]}
                  options={options}
                  onSelect={(v) => handleSelect(key, v)}
                />
              );
            })}
          </div>

          <BaseButton
            size='s'
            onClick={handleApply}
            width='100%'
            hideIcon
            isLoading={isLoading}
          >
            Apply filters
          </BaseButton>
        </div>
      </div>
    </>
  );
};

export default FilterModal;
